import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { AuthService, StateUtils, StateWithProcess } from '@spartacus/core';
import { Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, share, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth';
import { AccountingCode, AccountingCodeList, OrderEntry } from '../../../../core/model';
import { Cart, CartError, CartErrorContext, CartType } from '../../../../core/model/cart.model';
import { PrincipalConfigurationService } from '../../../../core/user';
import { withdrawOn } from '../../../../core/util';
import { ActiveCartFacade, CartAccountingCodesFacade, CartFacade } from '../facades';
import { CartActions } from '../store/actions';
import { AccountingCodesState, StateWithCart } from '../store/cart-state';
import { CartSelectors } from '../store/selectors';

@Injectable()
export class CartAccountingCodesService implements CartAccountingCodesFacade {
  constructor(
    private store: Store<StateWithCart & StateWithProcess<any>>,
    private userIdService: UserIdService,
    private activeCartService: ActiveCartFacade,
    private cartService: CartFacade,
    private authService: AuthService,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}

  isAccountingCodeModalOpen(): Observable<boolean> {
    return this.store.pipe(select(CartSelectors.getAccountingCodeModalOpen));
  }

  getAccountingCodesState(): Observable<AccountingCodesState> {
    return this.store.pipe(
      select(CartSelectors.getAccountingCodesState),
      filter((accountingCode) => !!accountingCode)
    );
  }

  getActiveAccountingCode(): Observable<AccountingCode> {
    return this.store.pipe(
      select(CartSelectors.getActiveAccountingCode),
      filter((accountingCode) => !!accountingCode)
    );
  }

  setAccountingCodesModalOpen(cartType: CartType, accountingCode: AccountingCode, isModalOpen: boolean): void {
    this.activeCartService
      .getActiveCart(cartType)
      .pipe(
        take(1),
        filter((cart) => cart.entries.length > 1)
      )
      .subscribe(() => {
        this.store.dispatch(new CartActions.SetAccountingCodesModalOpen({ key: cartType, active: accountingCode, isModalOpen }));
      });
  }

  isAccountingCodesModalDismissed(): Observable<boolean> {
    return this.store.pipe(select(CartSelectors.getAccountingCodeModalDismissed));
  }

  setAccountingCodesModalDismissed(isDismissed: boolean, cartType: CartType): void {
    this.store.dispatch(new CartActions.SetAccountingCodesModalDismissed({ key: cartType, isModalDismissed: isDismissed }));
  }

  setAccountingCodeForAllEntries(cartType: CartType): void {
    combineLatest([this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)])
      .pipe(take(1))
      .subscribe(([userId, cartId]) => {
        this.store.dispatch(new CartActions.SetAccountingCodeForAllEntries({ cartId, userId, key: cartType }));
      });
  }

  setAccountingCodeForAllEntriesAndRemoveIneligibleEntries(cartType: CartType): void {
    combineLatest([this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)])
      .pipe(take(1))
      .subscribe(([userId, cartId]) => {
        this.store.dispatch(
          new CartActions.SetAccountingCodeForAllEntriesAndRemoveIneligibleEntries({ cartId, userId, key: cartType })
        );
      });
  }

  updateEntryAccountingCode(entry: OrderEntry, accountingCode: string, cartType: CartType): void {
    combineLatest([this.userIdService.takeUserId(true), this.activeCartService.getActiveCartId(cartType)])
      .pipe(take(1))
      .subscribe(([userId, cartId]) => {
        this.cartService.updateEntryAccountingCode(userId, cartType, cartId, entry, accountingCode);
      });
  }

  getUpdateEntryAccountingCode(entry: OrderEntry, cartType: CartType): Observable<AccountingCode | undefined> {
    return this.activeCartService.getActiveCartId(cartType).pipe(
      switchMap((cartId) => this.cartService.getUpdateEntryAccountingCode(cartId, entry)),
      distinctUntilChanged((prev, curr) => prev.code === curr.code)
    );
  }

  loadAccountingCodeList(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new CartActions.LoadAccountingCodeList(userId)),
      () => {}
    );
  }

  private getAccountingCodeListState(): Observable<StateUtils.LoaderState<AccountingCodeList>> {
    return this.store.select(CartSelectors.getAccountingCodeListLoaderState);
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<AccountingCodeList>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }

  getAccountingCodeList(): Observable<AccountingCodeList | undefined> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.getAccountingCodeListState()),
      filter((loaderState) => {
        if (this.needsToLoad(loaderState)) {
          this.loadAccountingCodeList();
          return false;
        }
        return loaderState.success;
      }),
      map((loaderState) => loaderState.value),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn))),
      take(1)
    );
  }

  getAccountingCodeListLoading(): Observable<boolean> {
    return this.store.pipe(select(CartSelectors.getAccountingCodeListLoading));
  }

  getAccountingCodesMandatoryButNoneAvailable(): Observable<boolean> {
    return this.principalConfigurationService.isEnabled('enableAccountingCodes').pipe(
      switchMap((enableAccountingCodes) =>
        enableAccountingCodes
          ? this.getAccountingCodeList().pipe(
              withLatestFrom(this.principalConfigurationService.isEnabled('enableMandatoryAccountingCodes')),
              map(
                ([accountingCodeList, enableMandatoryAccountingCode]) =>
                  !!enableMandatoryAccountingCode && accountingCodeList.accountingCodes.length === 0
              )
            )
          : of(false)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getEntryAccountingCodeErrors(
    accountingCode: AccountingCode | undefined,
    validAccountingCodes: AccountingCode[] | undefined,
    allAccountingCodeEntries: AccountingCode[],
    enableAccountingCodes: boolean,
    enableMandatoryAccountingCodes: boolean
  ): ValidationErrors | null {
    if (!enableAccountingCodes) {
      return null;
    }

    if (accountingCode) {
      if (!validAccountingCodes?.some((validAccountingCode) => validAccountingCode.code === accountingCode?.code)) {
        return { invalidAccountingCodeAssigned: { valid: false } };
      }

      const isReinvoicing = accountingCode?.reinvoicing ?? false;

      const formHasReinvoicingEntries = allAccountingCodeEntries.some((c) => c?.reinvoicing);

      //If there are reinvoicing fields, and this is not a reinvoicing field, the field is not valid
      if (formHasReinvoicingEntries && !isReinvoicing) {
        return { notReinvoicingFieldValues: { valid: false } };
      }
    } else if (enableMandatoryAccountingCodes) {
      return { required: { valid: false } };
    }

    return null;
  }

  getCartAccountingCodeErrors(cart: Cart): Observable<CartError[] | null> {
    if (!cart) {
      return of(null);
    }
    const allAccountingCodeEntries = cart.entries?.map((entry) => entry?.accountingCode);
    return combineLatest([
      this.principalConfigurationService.isEnabled('enableAccountingCodes'),
      this.principalConfigurationService.isEnabled('enableMandatoryAccountingCodes'),
    ]).pipe(
      switchMap(([enableAccountingCodes, enableMandatoryAccountingCodes]) => {
        if (!enableAccountingCodes) {
          return of(null);
        }
        return this.getAccountingCodeList().pipe(
          map((accountingCodeList) => {
            return cart.entries?.reduce((errors: CartError[] | null, entry) => {
              const entryErrors = this.getEntryAccountingCodeErrors(
                entry?.accountingCode,
                entry?.accountingCodes?.length ? entry.accountingCodes : (accountingCodeList?.accountingCodes ?? []),
                allAccountingCodeEntries,
                enableAccountingCodes,
                enableMandatoryAccountingCodes
              );
              if (entryErrors) {
                return [
                  ...(errors ?? []),
                  <CartError>{ context: CartErrorContext.ACCOUNTING_CODE, entryId: entry?.id, error: entryErrors },
                ];
              }
              return errors;
            }, null);
          })
        );
      })
    );
  }
}
